// src/Context/AuthContext.js
import { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../assets/images/logo-dark.png";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();

    const SESSION_EXPIRATION_MS = 60 * 60 * 1000; // 1 hour

    const defaultUser = useMemo(() => ({
        LEDGER_ID: "00000000-0000-0000-0000-000000000000",
        TOKEN: null,
        COMPANYLOGO: Logo,
        COMPANYNAME: "MJH",
        USERNAME: "RACHIT",
    }), []);

    const [user, setUser] = useState(defaultUser);
    const [loading, setLoading] = useState(true);

    // Load user from localStorage on mount
    useEffect(() => {
        try {
            const storedData = localStorage.getItem("LOGIN_RESPONSE");
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                parsedData.COMPANYLOGO = parsedData.COMPANYLOGO || Logo;
                parsedData.COMPANYNAME = parsedData.COMPANYNAME || "MJH";
                parsedData.USERNAME = parsedData.USERNAME || "RACHIT";
                setUser(parsedData);

                // Setup session expiration
                const loginTime = localStorage.getItem("LOGIN_TIME");
                if (loginTime && Date.now() - parseInt(loginTime, 10) > SESSION_EXPIRATION_MS) {
                    logout(); // session expired
                }
            }
        } catch (error) {
            console.error("Failed to parse user data from localStorage:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Login: update state + localStorage + redirect
    const login = useCallback((userData) => {
        try {
            userData.COMPANYLOGO = userData.COMPANYLOGO || Logo;
            userData.COMPANYNAME = userData.COMPANYNAME || "MJH";
            userData.USERNAME = userData.USERNAME || "RACHIT";

            localStorage.setItem("LOGIN_RESPONSE", JSON.stringify(userData));
            localStorage.setItem("LOGIN_TIME", Date.now().toString());
            if (userData.TOKEN) localStorage.setItem("authToken", userData.TOKEN);
            localStorage.removeItem("LOGGED_OUT");

            setUser(userData);

            // Redirect after login
        } catch (error) {
            console.error("Failed to save user data to localStorage:", error);
        }
    }, [navigate]);

    // Logout: clear state + localStorage + redirect
    const logout = useCallback(() => {
        try {
            //   localStorage.removeItem("LOGIN_RESPONSE");
            //   localStorage.removeItem("authToken");
            //   localStorage.removeItem("LOGIN_TIME");
            //   localStorage.removeItem("LOGGED_OUT");

            localStorage.clear()
            setUser(defaultUser);

            navigate("/login", { replace: true });
        } catch (error) {
            console.error("Failed to clear session:", error);
        }
    }, [defaultUser, navigate]);

    // Check if user is logged in
    const checkIsUserLoggedIn = useCallback(() => {
        return user.LEDGER_ID !== defaultUser.LEDGER_ID;
    }, [user, defaultUser]);

    // Update company logo
    const updateCompanyLogo = useCallback((newLogoURL) => {
        setUser((prevUser) => {
            const updatedUser = { ...prevUser, COMPANYLOGO: newLogoURL };
            localStorage.setItem("LOGIN_RESPONSE", JSON.stringify(updatedUser));
            return updatedUser;
        });
    }, []);

    // Auto logout after session expiration
    useEffect(() => {
        if (checkIsUserLoggedIn()) {
            const loginTime = parseInt(localStorage.getItem("LOGIN_TIME") || "0", 10);
            const remainingTime = SESSION_EXPIRATION_MS - (Date.now() - loginTime);

            if (remainingTime <= 0) {
                logout();
            } else {
                const timer = setTimeout(() => {
                    logout();
                }, remainingTime);

                return () => clearTimeout(timer);
            }
        }
    }, [user, logout, checkIsUserLoggedIn]);

    const contextValue = useMemo(() => ({
        user,
        login,
        logout,
        checkIsUserLoggedIn,
        updateCompanyLogo,
        loading,
    }), [user, login, logout, checkIsUserLoggedIn, updateCompanyLogo, loading]);

    return (
        <AuthContext.Provider value={contextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
